/**
 * Attribute Filter Parser
 * Converts between string syntax and AG-Grid filter model format
 *
 * Supported syntax:
 * - Simple: AF >= 0.5
 * - Multiple: (REF = 'C' OR REF = 'T') AND AF >= 0.3
 * - Range: 0.3 <= AF <= 0.7
 * - Text: SYMBOL CONTAINS 'BRCA'
 * - Complex: (IMPACT = 'HIGH' OR IMPACT = 'MODERATE') AND (0.3 <= AF <= 0.7)
 */

class AttributeFilterParser {
  constructor(gridRef) {
    this.gridRef = gridRef;

    // Token types
    this.TOKEN_TYPES = {
      COLUMN: "COLUMN",
      OPERATOR: "OPERATOR",
      VALUE: "VALUE",
      LOGICAL: "LOGICAL",
      LPAREN: "LPAREN",
      RPAREN: "RPAREN",
      NUMBER: "NUMBER",
      STRING: "STRING",
    };

    // Operators mapping - reusing AG-Grid's built-in filter options
    // These match the filterOptions defined in useVariantGrid.js
    this.OPERATORS = {
      "=": "equals",
      "<": "lessThan",
      ">": "greaterThan",
      "<=": "lessThanOrEqual",
      ">=": "greaterThanOrEqual",
    };

    // Reverse mapping for serialization
    this.OPERATORS_REVERSE = Object.entries(this.OPERATORS).reduce(
      (acc, [k, v]) => {
        acc[v] = k;
        return acc;
      },
      {}
    );
  }

  /**
   * Get column metadata from AG-Grid
   */
  getColumnMetadata() {
    if (!this.gridRef?.current?.api) return {};

    const columnDefs = this.gridRef.current.api.getColumnDefs();
    const metadata = {};

    columnDefs.forEach((group) => {
      if (group.children) {
        group.children.forEach((col) => {
          // Include columns from "Variant Info" and "Variant Site" groups
          const isVariantInfo = group.headerName === "Variant Info";
          const isVariantSite = group.headerName === "Variant Site";

          if (
            col.field &&
            col.filter !== false &&
            (isVariantInfo || isVariantSite)
          ) {
            metadata[col.field] = {
              type: col.cellDataType || "text",
              headerName: col.headerName,
              field: col.field,
              group: group.headerName,
            };
          }
        });
      }
    });

    return metadata;
  }

  /**
   * Parse string filter to AG-Grid model
   */
  parse(filterString) {
    if (!filterString || !filterString.trim()) {
      return null;
    }

    try {
      const tokens = this.tokenize(filterString);
      const ast = this.buildAST(tokens);
      return this.astToAgGrid(ast);
    } catch (error) {
      throw new Error(`Filter parsing error: ${error.message}`);
    }
  }

  /**
   * Tokenize the filter string
   */
  tokenize(filterString) {
    const tokens = [];
    let current = 0;
    const str = filterString.trim();

    while (current < str.length) {
      // Skip whitespace
      if (/\s/.test(str[current])) {
        current++;
        continue;
      }

      // Parentheses
      if (str[current] === "(") {
        tokens.push({ type: this.TOKEN_TYPES.LPAREN, value: "(" });
        current++;
        continue;
      }

      if (str[current] === ")") {
        tokens.push({ type: this.TOKEN_TYPES.RPAREN, value: ")" });
        current++;
        continue;
      }

      // String literals (single or double quotes)
      if (str[current] === "'" || str[current] === '"') {
        const quoteChar = str[current];
        let value = "";
        current++; // Skip opening quote

        while (current < str.length && str[current] !== quoteChar) {
          if (str[current] === "\\" && str[current + 1] === quoteChar) {
            value += quoteChar;
            current += 2;
          } else {
            value += str[current];
            current++;
          }
        }

        if (current >= str.length) {
          throw new Error("Unterminated string literal");
        }

        current++; // Skip closing quote
        tokens.push({ type: this.TOKEN_TYPES.STRING, value });
        continue;
      }

      // Numbers
      if (/[0-9.]/.test(str[current])) {
        let value = "";
        while (current < str.length && /[0-9.]/.test(str[current])) {
          value += str[current];
          current++;
        }
        tokens.push({
          type: this.TOKEN_TYPES.NUMBER,
          value: parseFloat(value),
        });
        continue;
      }

      // Multi-word operators removed - only basic comparison operators supported

      // Check for logical operators
      if (str.substr(current, 3).toUpperCase() === "AND") {
        tokens.push({ type: this.TOKEN_TYPES.LOGICAL, value: "AND" });
        current += 3;
        continue;
      }

      if (str.substr(current, 2).toUpperCase() === "OR") {
        tokens.push({ type: this.TOKEN_TYPES.LOGICAL, value: "OR" });
        current += 2;
        continue;
      }

      // CONTAINS operator removed - only basic comparison operators supported

      // Check for comparison operators
      const twoCharOp = str.substr(current, 2);
      if (["<=", ">=", "!="].includes(twoCharOp)) {
        tokens.push({ type: this.TOKEN_TYPES.OPERATOR, value: twoCharOp });
        current += 2;
        continue;
      }

      const oneCharOp = str[current];
      if (["<", ">", "="].includes(oneCharOp)) {
        tokens.push({ type: this.TOKEN_TYPES.OPERATOR, value: oneCharOp });
        current++;
        continue;
      }

      // Column names or keywords
      let value = "";
      while (current < str.length && /[a-zA-Z0-9_]/.test(str[current])) {
        value += str[current];
        current++;
      }

      if (value) {
        tokens.push({ type: this.TOKEN_TYPES.COLUMN, value });
        continue;
      }

      throw new Error(`Unexpected character: ${str[current]}`);
    }

    return tokens;
  }

  /**
   * Build Abstract Syntax Tree from tokens
   */
  buildAST(tokens) {
    let index = 0;

    const parseExpression = () => {
      return parseOr();
    };

    const parseOr = () => {
      let left = parseAnd();

      while (
        index < tokens.length &&
        tokens[index]?.type === this.TOKEN_TYPES.LOGICAL &&
        tokens[index].value === "OR"
      ) {
        index++; // Skip OR
        const right = parseAnd();
        left = { type: "OR", children: [left, right] };
      }

      return left;
    };

    const parseAnd = () => {
      let left = parseTerm();

      while (
        index < tokens.length &&
        tokens[index]?.type === this.TOKEN_TYPES.LOGICAL &&
        tokens[index].value === "AND"
      ) {
        index++; // Skip AND
        const right = parseTerm();
        left = { type: "AND", children: [left, right] };
      }

      return left;
    };

    // Helper functions for cleaner parsing
    const peek = (offset = 0) => tokens[index + offset];
    const consume = (expectedType, errorMsg) => {
      if (!peek() || peek().type !== expectedType) {
        throw new Error(errorMsg);
      }
      return tokens[index++];
    };
    const match = (type, value = null) => {
      const token = peek();
      return token?.type === type && (value === null || token.value === value);
    };

    const parseCondition = (column, operator, value) => ({
      type: "CONDITION",
      column,
      operator,
      value,
    });

    const parseRange = (leftValue, leftOp, column, rightOp, rightValue) => {
      const flipOperator = (op) =>
        ({ "<=": ">=", "<": ">", ">=": "<=", ">": "<" }[op]);
      return {
        type: "AND",
        children: [
          parseCondition(column, flipOperator(leftOp), leftValue),
          parseCondition(column, rightOp, rightValue),
        ],
      };
    };

    const parseTerm = () => {
      // Handle parentheses
      if (match(this.TOKEN_TYPES.LPAREN)) {
        index++; // Skip (
        const expr = parseExpression();
        consume(this.TOKEN_TYPES.RPAREN, "Missing ')'");
        return expr;
      }

      // Try to parse conditions starting with NUMBER or COLUMN
      if (match(this.TOKEN_TYPES.NUMBER)) {
        return parseNumberFirst();
      } else if (match(this.TOKEN_TYPES.COLUMN)) {
        return parseColumnFirst();
      }

      // Error cases
      if (match(this.TOKEN_TYPES.RPAREN)) {
        throw new Error("Extra closing parenthesis");
      } else if (match(this.TOKEN_TYPES.LOGICAL)) {
        throw new Error("Missing condition before logical operator");
      } else if (match(this.TOKEN_TYPES.OPERATOR)) {
        throw new Error("Operator without column name");
      } else {
        throw new Error(
          `Invalid syntax near: ${peek()?.value || "end of input"}`
        );
      }
    };

    const parseNumberFirst = () => {
      const leftValue = consume(
        this.TOKEN_TYPES.NUMBER,
        "Expected number"
      ).value;

      // Helper to check if current token is a comparison operator
      const isComparisonOperator = () =>
        match(this.TOKEN_TYPES.OPERATOR, "<=") ||
        match(this.TOKEN_TYPES.OPERATOR, ">=") ||
        match(this.TOKEN_TYPES.OPERATOR, "<") ||
        match(this.TOKEN_TYPES.OPERATOR, ">");

      // Helper to flip comparison operators for range syntax
      const flipOperator = (op) =>
        ({ "<=": ">=", "<": ">", ">=": "<=", ">": "<" }[op]);

      if (isComparisonOperator()) {
        const leftOp = consume(
          this.TOKEN_TYPES.OPERATOR,
          "Expected operator"
        ).value;

        if (match(this.TOKEN_TYPES.COLUMN)) {
          const column = consume(
            this.TOKEN_TYPES.COLUMN,
            "Expected column"
          ).value;

          // Check for complete range: NUM OP COL OP NUM
          if (isComparisonOperator()) {
            const rightOp = consume(
              this.TOKEN_TYPES.OPERATOR,
              "Expected operator"
            ).value;
            if (match(this.TOKEN_TYPES.NUMBER)) {
              const rightValue = consume(
                this.TOKEN_TYPES.NUMBER,
                "Expected number"
              ).value;
              return parseRange(leftValue, leftOp, column, rightOp, rightValue);
            }
          }

          // Partial range: NUM OP COL -> COL FLIPPED_OP NUM
          return parseCondition(column, flipOperator(leftOp), leftValue);
        }
      }

      throw new Error(`Invalid syntax after number ${leftValue}`);
    };

    const parseColumnFirst = () => {
      const column = consume(this.TOKEN_TYPES.COLUMN, "Expected column").value;
      const operator = consume(
        this.TOKEN_TYPES.OPERATOR,
        `Missing operator after '${column}'`
      ).value;

      let value;
      if (match(this.TOKEN_TYPES.STRING)) {
        value = consume(this.TOKEN_TYPES.STRING, "Expected string").value;
      } else if (match(this.TOKEN_TYPES.NUMBER)) {
        value = consume(this.TOKEN_TYPES.NUMBER, "Expected number").value;
      } else {
        throw new Error(`Missing value after '${operator}'`);
      }

      return parseCondition(column, operator, value);
    };

    const ast = parseExpression();

    if (index < tokens.length) {
      const remainingTokens = tokens
        .slice(index)
        .map((t) => t.value)
        .join(" ");
      throw new Error(`Unexpected tokens after expression: ${remainingTokens}`);
    }

    return ast;
  }

  /**
   * Convert AST to AG-Grid filter model
   */
  astToAgGrid(ast) {
    if (!ast) return null;

    const metadata = this.getColumnMetadata();

    const convert = (node) => {
      if (node.type === "CONDITION") {
        // Validate column exists
        if (!metadata[node.column]) {
          const availableColumns = Object.keys(metadata).join(", ");
          throw new Error(
            `Unknown column '${node.column}'. Available columns: ${availableColumns}`
          );
        }

        const colType = metadata[node.column].type;

        // Validate operator for column type
        if (colType === "number") {
          if (typeof node.value === "string" && isNaN(parseFloat(node.value))) {
            throw new Error(
              `Value '${node.value}' must be numeric for column '${node.column}'`
            );
          }
        } else if (colType === "text") {
          if (node.operator !== "=") {
            throw new Error(
              `Cannot use operator '${node.operator}' on text column '${node.column}'. Only = is supported for text columns.`
            );
          }
        }

        return {
          filterType: colType === "number" ? "number" : "text",
          colId: node.column,
          type: this.OPERATORS[node.operator] || node.operator,
          filter:
            colType === "number" && typeof node.value === "string"
              ? parseFloat(node.value)
              : node.value,
        };
      }

      if (node.type === "AND" || node.type === "OR") {
        return {
          filterType: "join",
          type: node.type,
          conditions: node.children.map(convert),
        };
      }

      throw new Error(`Unknown AST node type: ${node.type}`);
    };

    return convert(ast);
  }

  /**
   * Serialize AG-Grid model to string
   */
  serialize(agGridModel) {
    if (!agGridModel) return "";

    const serialize = (model, needsParens = false) => {
      // Handle leaf conditions
      if (model.filterType !== "join") {
        const operator = this.OPERATORS_REVERSE[model.type] || model.type;
        const value =
          typeof model.filter === "string" ? `'${model.filter}'` : model.filter;
        return `${model.colId} ${operator} ${value}`;
      }

      // Handle join conditions
      if (model.conditions && model.conditions.length > 0) {
        const parts = model.conditions.map((cond) => serialize(cond, true));
        const joined = parts.join(` ${model.type} `);
        return needsParens && model.conditions.length > 1
          ? `(${joined})`
          : joined;
      }

      return "";
    };

    return serialize(agGridModel);
  }

  /**
   * Validate balanced parentheses using stack algorithm
   */
  validateParentheses(filterString) {
    const stack = [];

    for (let i = 0; i < filterString.length; i++) {
      const char = filterString[i];

      if (char === "(") {
        stack.push(char);
      } else if (char === ")") {
        if (stack.length === 0) {
          return { valid: false, error: "Extra ')'" };
        }
        stack.pop();
      }
    }

    if (stack.length > 0) {
      return { valid: false, error: "Missing ')'" };
    }

    return { valid: true };
  }

  /**
   * Validate a filter string without parsing completely
   */
  validate(filterString) {
    if (!filterString.trim()) {
      return { valid: true };
    }

    // First check parentheses balance
    const parenCheck = this.validateParentheses(filterString);
    if (!parenCheck.valid) {
      return parenCheck;
    }

    try {
      this.parse(filterString);
      return { valid: true };
    } catch (error) {
      return {
        valid: false,
        error: error.message,
      };
    }
  }

  /**
   * Get available columns for autocomplete
   */
  getAvailableColumns() {
    const metadata = this.getColumnMetadata();
    return Object.entries(metadata).map(([field, meta]) => ({
      label: `${meta.group}: ${meta.headerName} (${field})`,
      value: field,
      type: meta.type,
      group: meta.group,
      headerName: meta.headerName,
      displayName: `${meta.group}: ${meta.headerName} (${field})`,
    }));
  }

  /**
   * Get valid operators for a column
   */
  getOperatorsForColumn(columnField) {
    const metadata = this.getColumnMetadata();
    const colMeta = metadata[columnField];

    if (!colMeta) return ["="];

    if (colMeta.type === "number") {
      return Object.keys(this.OPERATORS);
    } else if (colMeta.type === "text") {
      return ["="];
    }

    return ["="];
  }
}

export default AttributeFilterParser;
